// Server-Heartbeat: liest den "ping"-Channel (Backend publisht alle 5s
// {ts: <server-zeit>}). Daraus eine Uhr, die die SERVER-Zeit zeigt und
// sichtbar einfriert + rot wird, wenn der Server nicht mehr atmet (kein
// Ping mehr ankommt). So sieht man auf der System-Karte sofort, ob der
// Server lebt -- eine reine Browser-Uhr wuerde stur weiterticken.
//
// Anzeige-Zeit wird zwischen den 5s-Pings lokal interpoliert (Offset
// server-lokal), damit die Uhr fluessig sekundenweise laeuft. Bleibt der
// Ping aus (> STALE_MS), frieren wir auf die letzte Server-Zeit ein.

import { subscribe as wsSubscribe, wsConnected } from "./ws-hub.svelte";

// Backstop-Fenster fuer halb-offene Verbindungen (TCP haengt, kein
// sauberes onclose). Die ueblichen Faelle (Netz weg / Server-Stopp)
// erkennen wir VORHER instant ueber navigator.onLine bzw. das ws-onclose
// -- dieser Timeout greift nur, wenn beides ausbleibt. 3x Ping (2s) = 6s.
const STALE_MS = 6_000;

let _offsetMs = 0; // serverTs - localRecv, zum Interpolieren
let _lastServerMs = 0; // letzte empfangene Server-Zeit (ms)
let _lastBeatLocal = 0; // Date.now() beim letzten Ping
let _hadBeat = $state(false);
let _tick = $state(0); // 1s-Ticker treibt die Reaktivitaet der Getter
// Hat das GERAET selbst Netz? navigator.onLine unterscheidet "mein WLAN/
// LAN ist weg" von "Server antwortet nicht" -- man will gleich wissen,
// auf welcher Seite das Problem sitzt.
let _online = $state(true);

let _unsub: (() => void) | undefined;
let _timer: ReturnType<typeof setInterval> | undefined;
let _started = false;

function _setOnline(): void {
  _online = true;
}
function _setOffline(): void {
  _online = false;
}

function onPing(data: unknown): void {
  const ts = (data as { ts?: string } | undefined)?.ts;
  if (!ts) return;
  const serverMs = Date.parse(ts);
  if (Number.isNaN(serverMs)) return;
  const localNow = Date.now();
  _offsetMs = serverMs - localNow;
  _lastServerMs = serverMs;
  _lastBeatLocal = localNow;
  _hadBeat = true;
}

export function startHeartbeat(): void {
  if (_started) return;
  _started = true;
  _unsub = wsSubscribe("ping", onPing);
  _timer = setInterval(() => {
    _tick = (_tick + 1) % 1_000_000;
  }, 1000);
  if (typeof navigator !== "undefined") _online = navigator.onLine;
  if (typeof window !== "undefined") {
    window.addEventListener("online", _setOnline);
    window.addEventListener("offline", _setOffline);
  }
}

export function stopHeartbeat(): void {
  _started = false;
  _unsub?.();
  _unsub = undefined;
  if (_timer) clearInterval(_timer);
  _timer = undefined;
  if (typeof window !== "undefined") {
    window.removeEventListener("online", _setOnline);
    window.removeEventListener("offline", _setOffline);
  }
}

export type ConnState = "init" | "alive" | "no_net" | "server_unreachable";

/**
 * init = noch kein Ping seit Laden; alive = Heartbeat frisch;
 * no_net = Heartbeat weg UND Geraet offline (eigenes WLAN/LAN weg);
 * server_unreachable = Heartbeat weg, aber Geraet online (Server/Pfad weg).
 * Die Unterscheidung sagt sofort, auf welcher Seite das Problem sitzt.
 */
export function connectivityState(): ConnState {
  void _tick; // dep-tracking: jede Sekunde neu bewerten
  if (!_hadBeat) return "init";
  // Instant-Pfade (keine Timeout-Wartezeit):
  if (!_online) return "no_net"; // navigator 'offline' feuert sofort
  if (!wsConnected()) return "server_unreachable"; // ws onclose feuert sofort
  // Backstop fuer halb-offene Sockets, bei denen beides ausbleibt:
  if (Date.now() - _lastBeatLocal >= STALE_MS) return "server_unreachable";
  return "alive";
}

/**
 * Anzuzeigende Uhr-Zeit (ms). Bei alive interpoliert aus Server-Offset,
 * bei stale eingefroren auf die letzte Server-Zeit, bei init null
 * (Caller faellt auf die Browser-Zeit zurueck, bis der erste Ping da ist).
 */
export function serverClockMs(): number | null {
  void _tick;
  if (!_hadBeat) return null;
  if (Date.now() - _lastBeatLocal < STALE_MS) return Date.now() + _offsetMs;
  return _lastServerMs;
}
